import React, { useEffect, useRef, useState, useMemo } from "react";
import mapboxGeocoding from "@mapbox/mapbox-sdk/services/geocoding";
import InputField from "components/fields/InputField";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapboxAddressInput = ({
  label,
  id,
  value,
  onResult,
  required,
  onClearInput,
  placeholder,
}) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Memoize geocoding client to prevent creating new instances on every render
  const geocodingClient = useMemo(
    () => mapboxGeocoding({ accessToken: MAPBOX_TOKEN }),
    []
  );

  // Fetch suggestions when query changes
  useEffect(() => {
    if (!query || !isFocused) return;

    const controller = new AbortController();
    geocodingClient
      .forwardGeocode({
        query,
        autocomplete: true,
        limit: 5,
        // Prioritize POI but also include addresses and places
        types: ['poi', 'address', 'place'],
      })
      .send()
      .then((response) => {
        const results = response.body.features;
        setSuggestions(results);
      })
      .catch(() => setSuggestions([]));

    return () => controller.abort();
  }, [query, isFocused, geocodingClient]);

  const handleSelect = (place) => {
    const context = place?.context || [];
    const getContextText = (idPrefix) =>
      context?.find((c) => c?.id?.startsWith(idPrefix))?.text || "";

    // Extract location name - for POIs this will be the business/landmark name
    // place.text contains the primary name (e.g., "Starbucks", "Central Park")
    const locationName = place?.text || "";

    // Extract street address with house number
    // For addresses, place.address contains the house number
    // and place.text contains the street name
    let streetAddress = "";
    if (place?.address) {
      // If we have a house number, combine it with the street name
      streetAddress = `${place.address} ${place.text}`;
    } else if (place?.text) {
      // Otherwise just use the text (could be a POI or street without number)
      streetAddress = place.text;
    }

    const selectedData = {
      address: place?.place_name,
      locationName: locationName, // Save the location/POI name
      state: getContextText("region"),
      city: getContextText("place"),
      street: streetAddress,
      zipCode: getContextText("postcode"),
      lat: place?.center[1],
      lng: place?.center[0],
    };

    onResult(selectedData);

    // Close dropdown and set selected value
    setQuery(place.place_name);
    setSuggestions([]);
    setIsFocused(false);
    inputRef.current.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      setActiveIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    }
  };

  useEffect(() => {
    if (value) {
      setQuery(value);
    }
  }, [value]);

  return (
    <div className="flex flex-col pt-4">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-fg">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-danger-fg">
            *
          </span>
        )}
      </label>

      <div className="relative">
        <InputField
          type="text"
          id={id}
          ref={inputRef}
          placeholder={placeholder || "Search by name or address"}
          value={query}
          onChange={(e) => {
            const inputValue = e.target.value;
            setQuery(inputValue);
            if (inputValue === "" && onClearInput) {
              onClearInput(); // Clear data only when input is empty
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setTimeout(() => {
              setSuggestions([]);
              setIsFocused(false);
            }, 150);
          }}
          autoComplete="off"
        />

        {isFocused && suggestions.length > 0 && (
          <ul
            role="listbox"
            className="absolute top-full z-10 mt-1 w-full max-w-lg overflow-hidden rounded-control border border-line bg-surface text-fg shadow-overlay"
          >
            {suggestions?.map((s, i) => (
              <li
                key={s.id}
                role="option"
                aria-selected={i === activeIndex}
                className={`cursor-pointer px-3 py-2 text-sm hover:bg-surface-subtle ${
                  i === activeIndex ? "bg-surface-subtle" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent blur before select
                  handleSelect(s);
                }}
              >
                {s.place_name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MapboxAddressInput;
